// Chat.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import DropDownBox from "devextreme-react/drop-down-box";
import DataGrid, { Selection, Paging, FilterRow, Scrolling } from "devextreme-react/data-grid";
import { Reset_Change_Receipt_Items } from "../../../redux/actions/receipt";
import ChatStyles from "./ChatStyles";
import { AppSettingsLocalStorageKey, ChatMessagesStoragePrefix, ChatConversationStoragePrefix } from "../../../const/constants";
import { v4 as uuidv4 } from "uuid";
import * as PermissionsConstants from "../../../const/constants";
import FeedbackModal from '../../../common/FeedbackModal';
import { sanitizeHtml, withLineBreaks } from '../../../common/sanitizeHtml';
import { handleReportFileLinkClick } from '../../../common/Utils';
import data from "../../../assets/apiRoutes.json";

// API endpoint for grid options. Expects array of { ID, name } (or id, name).
const GRID_OPTIONS_API = "/ai/chat/tree-options";
const gridColumns = ["Name"];

const Chat = (props) => {
  var permissions = props.ReceiptReducer.rolePermissions;
  const viewVectorStoresForChat = permissions.AIChat?.includes(PermissionsConstants.AIChatSelectVectorStoresForChat);

  const dispatch = useDispatch();
  // oidc comes from Redux via mapStateToProps, or can be passed as prop
  const oidc = props.oidc;
  const accessToken = props.oidc?.user?.access_token;
  const history = useHistory();
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [deployment, setDeployment] = useState("");
  const [company, setCompany] = useState("");
  const [model, setModel] = useState("");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [gridBoxValue, setGridBoxValue] = useState([]);
  const [gridOptions, setGridOptions] = useState([]);
  const suggestionCancellation = useRef(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    try {
      const settingsStr = localStorage.getItem(AppSettingsLocalStorageKey);
      if (settingsStr) {
        const settings = JSON.parse(settingsStr);
        setModel(settings.Model || "");
        setDeployment(settings.ModelDeployment || "");
        setCompany(settings.ModelCompany || "");
      }

      axios.get(`${window._env_.REACT_APP_APIS}/ai/vectorstore/GetVectorStores`, {
          headers: {
              access_token: accessToken,
              Authorization: `Bearer ${accessToken}`
          }
      }).then((res) => {

        setGridOptions(res.data.data);
      }).catch((err) => {
        console.error('Failed to get vector store:', err);

      });
    } 
    catch (err) 
    {
      console.warn("Failed to load app settings:", err);
    }
  }, []);
  
  const syncDataGridSelection = useCallback((e) => {
    setGridBoxValue(e.value ?? []);
  }, []);

  const dataGridOnSelectionChanged = useCallback((e) => {
    setGridBoxValue((e.selectedRowKeys?.length && e.selectedRowKeys) ?? []);
  }, []);

  const dataGridRender = useCallback(
    () => (
      <DataGrid
        height={345}
        dataSource={gridOptions}
        keyExpr="Id"
        columns={gridColumns}
        hoverStateEnabled={true}
        selectedRowKeys={gridBoxValue}
        onSelectionChanged={dataGridOnSelectionChanged}
      >
        <Selection mode="multiple" />
        <Scrolling mode="virtual" />
        <Paging enabled pageSize={10} />
        <FilterRow visible />
      </DataGrid>
    ),
    [gridOptions, gridBoxValue, dataGridOnSelectionChanged]
  );

  const updateSuggestions = async () => {
    if (suggestionCancellation.current) suggestionCancellation.current.cancel();
    suggestionCancellation.current = axios.CancelToken.source();
    try {
      const response = [];
      setSuggestions(response);
    } catch (err) {
      if (!axios.isCancel(err)) setSuggestions([]);
    }
  };

  const receiptTabIndex = 0;
  const shipmentTabIndex = 1;
  const shipment = "&transactionType=S";
  const receipt = "&transactionType=R";

  const openShipmentTransaction = (rowId, customerId, type) => {
    dispatch(Reset_Change_Receipt_Items([]));
    const normalizedType = (type || "").trim();
    const isShipment = normalizedType === shipment || normalizedType === "S";
    const isReceipt = normalizedType === receipt || normalizedType === "R";
    if (isShipment) {
      props.setSelectedTabIndex(shipmentTabIndex);
      history.push(`/wm/transaction/change-shippment?rowId=${rowId}&customerId=${customerId}`);
    } else if (isReceipt) {
      props.setSelectedTabIndex(receiptTabIndex);
      history.push(`/wm/transaction/change-receipt?rowId=${rowId}&customerId=${customerId}`);
    }
  };

  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target.closest(".route-link");
      if (target) {
        event.preventDefault();
        const rowId = target.getAttribute("data-rowid");
        const customerId = target.getAttribute("data-customerid");
        const type = target.getAttribute("data-type");
        if (rowId && customerId) openShipmentTransaction(rowId, customerId, type);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const [messages, setMessages] = useState([]);
  const [currentResponseMessage, setCurrentResponseMessage] = useState(null);
  const [requestStartTime, setRequestStartTime] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const chatInputRef = useRef(null);

  useEffect(() => {
    if (!requestStartTime) return;
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - requestStartTime);
    }, 100);
    return () => clearInterval(interval);
  }, [requestStartTime]);

  const typeMessage = async (fullText, onUpdate, containerRef, delay = 25) => {
    for (let i = 0; i <= fullText.length; i++) {
      onUpdate(fullText.slice(0, i));
      await new Promise((r) => setTimeout(r, delay));
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }
  };

  const addUserMessageAsync = async (userMessage) => {
    setMessages((prev) => [...prev, userMessage]);
    setSuggestions([]);
    setCurrentResponseMessage({ role: "assistant", content: "" });
    setRequestStartTime(Date.now());
    setElapsedMs(0);

    const startTime = Date.now();

    try {
      const historyToSend = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await axios.post(
        `${window._env_.REACT_APP_APIS}/ai/chat/send`,
        {
          Message: userMessage.content,
          Messages: historyToSend,
          VectorStoreIds: gridBoxValue,
          ConversationId: ensureConversationId()
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { access_token: accessToken } : {}),
          },
        }
      );
      const fullResponse = res?.data?.response ?? "";
      // Kept on the message so a Like or Dislike can find this turn's log row - and, because messages are
      // persisted, still find it after a reload. Absent for answers from before the log existed.
      const turnId = res?.data?.turnId ?? null;
      const requestTimeMs = Date.now() - startTime;


      /*await typeMessage(
        fullResponse,
        (partial) => {
          setCurrentResponseMessage({ role: "assistant", content: partial });
        },
        containerRef,
        25
      );*/

      setCurrentResponseMessage({ role: "assistant", content: fullResponse, requestTimeMs });

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: fullResponse, requestTimeMs, turnId },
      ]);
      
      setCurrentResponseMessage(null);
      setRequestStartTime(null);
      updateSuggestions();
    } catch (err) {
      const requestTimeMs = Date.now() - startTime;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Error: " + err.message, requestTimeMs },
      ]);
      setCurrentResponseMessage(null);
      setRequestStartTime(null);
    }
  };

  const getChatStorageKey = () => {
    const userId = props.oidc?.user?.profile?.sub || props.oidc?.user?.sub || 'default';
    return `${ChatMessagesStoragePrefix}${userId}`;
  };

  const currentUserId = props.oidc?.user?.profile?.sub || props.oidc?.user?.sub || 'default';

  const getConversationStorageKey = () => `${ChatConversationStoragePrefix}${currentUserId}`;

  // The conversation this chat belongs to. Only the "+" (New Chat) button ends one - nothing time-based
  // does - so it survives reloads and days. Read from storage on every call and never cached: <Chat> is
  // mounted twice at once (this page and the sidebar), both share the key, and a cached id would let the
  // two disagree the moment either one resets.
  const ensureConversationId = () => {
    const key = getConversationStorageKey();
    try {
      let id = localStorage.getItem(key);
      if (!id) {
        id = uuidv4();
        localStorage.setItem(key, id);
      }
      return id;
    } catch {
      // Storage unavailable (private mode, blocked): send none, and the server mints one for the turn.
      // Each turn then stands alone, which is the honest result when nothing can remember the thread.
      return null;
    }
  };

  useEffect(() => {
    const storageKey = getChatStorageKey();
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        setMessages(JSON.parse(saved));
      } catch { 
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId) {
      const storageKey = getChatStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(messages));
    }
  }, [messages, currentUserId]);

  const resetConversationAsync = () => {
    setMessages([]);
    setSuggestions([]);
    setCurrentResponseMessage(null);
    if (chatInputRef.current) chatInputRef.current.value = "";
    const storageKey = getChatStorageKey();
    localStorage.removeItem(storageKey);
    // "+" is the one conversation boundary. Mint the next id now rather than on the next send, so the other
    // mounted <Chat> cannot send one more turn under the conversation that was just closed.
    localStorage.removeItem(getConversationStorageKey());
    ensureConversationId();
  };

  const containerRef = useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, currentResponseMessage]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!chatInputRef.current) return;
    const content = chatInputRef.current.value.trim();
    if (content.length === 0) return;
    addUserMessageAsync({ role: "user", content });
    chatInputRef.current.value = "";
  };

  return (
    <div style={ChatStyles.app}>
      <header style={ChatStyles.header}>
        <button
          style={ChatStyles.headerButton}
          aria-label="New Chat"
          title="New Chat"
          onClick={resetConversationAsync}
        >
          +
        </button>

        <div style={{ flex: 1 }}>
          <div
            className="chat-header-description"
            style={{
              padding: "0 1rem",
              color: "#666",
              textAlign: "center",
              whiteSpace: "pre-line",
            }}
          >
            {`Hello! I'm your AI assistant.\nHow can I help you today?.`}
          </div>
        </div>
      </header>

      <main ref={containerRef} style={ChatStyles.messageListContainer}>
        <div style={ChatStyles.innerContainer}>
          {messages.map((m, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "0.25rem",
                marginBottom: "0.5rem",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                <div style={ChatStyles.iconWrapper}>
                  {m.role === "user" ? (
                    <i className="fa fa-user" style={ChatStyles.userIcon} />
                  ) : (
                    <i className="fa fa-comment" style={ChatStyles.botIcon} />
                  )}
                </div>

                <div
                  style={{
                    ...ChatStyles.message,
                    ...(m.role === "user"
                      ? ChatStyles.userMessage
                      // The assistant's content is HTML and breaks with <br />, so the newlines that
                      // merely separate those tags in the source must collapse. Under the inherited
                      // pre-wrap they each rendered as another blank line: one intended break between
                      // two shipments came out as five.
                      : { ...ChatStyles.assistantMessage, whiteSpace: "normal" }),
                  }}
                  dangerouslySetInnerHTML={
                    m.role === "assistant"
                      ? { __html: sanitizeHtml(withLineBreaks(m.content)) }
                      : undefined
                  }
                  onClick={m.role === "assistant" ? (e) => handleReportFileLinkClick(e, accessToken) : undefined}
                >
                  {m.role === "user" ? m.content : null}
                </div>
              </div>

              {m.role === "assistant" && (
                <div style={{ display: "flex", gap: "0.5rem", paddingLeft: "60px", alignItems: "center", flexWrap: "wrap" }}>
                  {m.requestTimeMs != null && (
                    <span style={{ fontSize: "0.75rem", color: "#888", marginRight: "0.5rem" }} title="Time spent on this request">
                      {m.requestTimeMs >= 1000
                        ? `${(m.requestTimeMs / 1000).toFixed(1)}s`
                        : `${m.requestTimeMs}ms`}
                    </span>
                  )}
                  <button
                    style={{
                      fontSize: "0.75rem",
                      alignSelf: "flex-start",
                      cursor: "pointer",
                      border: "none",
                      background: "transparent",
                      color: copiedMessageIndex === idx ? "#19c28f" : "#888",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      paddingLeft:"0px"
                    }}
                    title={copiedMessageIndex === idx ? "Copied!" : "Copy response"}
                    onClick={() => {
                      navigator.clipboard.writeText(m.content);
                      setCopiedMessageIndex(idx);
                      setTimeout(() => setCopiedMessageIndex(null), 2000);
                    }}
                  >
                    <i className={copiedMessageIndex === idx ? "fa fa-check" : "fa fa-copy"} />
                  </button>
                  <button
                    style={{
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      border: "none",
                      background: "transparent",
                      color: "#888",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                    title="Like"
                    onClick={() => {
                      const userName = oidc?.user?.profile?.name || oidc?.user?.profile?.preferred_username || oidc?.user?.profile?.email || '';
                      const feedbackData = {
                        userId: userName,
                        text: m.content,
                        action: "Like",
                        // Null for answers from before the usage log: still accepted, recorded as an orphan.
                        turnId: m.turnId ?? null,
                        conversationId: ensureConversationId()
                      };
                      console.log("Like button clicked - onOpenFeedback exists:", !!props.onOpenFeedback, "feedbackData:", feedbackData);
                      if (props.onOpenFeedback) {
                        try {
                          props.onOpenFeedback(feedbackData);
                          console.log("onOpenFeedback called successfully");
                        } catch (error) {
                          console.error("Error calling onOpenFeedback:", error);
                        }
                      } else {
                        // Fallback: show feedback modal when callback is not provided
                        setFeedbackData(feedbackData);
                        setShowFeedbackModal(true);
                      }
                    }}
                  >
                    <i className="fa fa-thumbs-up" />
                  </button>
                  <button
                    style={{
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      border: "none",
                      background: "transparent",
                      color: "#888",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                    title="Dislike"
                    onClick={() => {
                      const userName = oidc?.user?.profile?.name || oidc?.user?.profile?.preferred_username || oidc?.user?.profile?.email || '';
                      const feedbackData = {
                        userId: userName,
                        text: m.content,
                        action: "Dislike",
                        turnId: m.turnId ?? null,
                        conversationId: ensureConversationId()
                      };
                      console.log("Dislike button clicked - onOpenFeedback exists:", !!props.onOpenFeedback, "feedbackData:", feedbackData);
                      if (props.onOpenFeedback) {
                        try {
                          props.onOpenFeedback(feedbackData);
                          console.log("onOpenFeedback called successfully");
                        } catch (error) {
                          console.error("Error calling onOpenFeedback:", error);
                        }
                      } else {
                        // Fallback: show feedback modal when callback is not provided
                        setFeedbackData(feedbackData);
                        setShowFeedbackModal(true);
                      }
                    }}
                  >
                    <i className="fa fa-thumbs-down" />
                  </button>
                </div>
              )}
            </div>
          ))}


          {currentResponseMessage && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.75rem",
                marginBottom: "0.5rem",
                width: "100%",
              }}
            >
              <div style={ChatStyles.iconWrapper}>
                <i className="fa fa-comment" style={ChatStyles.botIcon} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <div
                  style={{
                    ...ChatStyles.message,
                    ...ChatStyles.assistantMessage,
                    opacity: 0.6,
                  }}
                >
                  {currentResponseMessage.content || <TypingIndicator />}
                </div>
                {requestStartTime != null && (
                  <span style={{ fontSize: "0.75rem", color: "#888", paddingLeft: "0.25rem" }}>
                    {elapsedMs >= 1000 ? `${(elapsedMs / 1000).toFixed(1)}s` : `${elapsedMs}ms`}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <form style={ChatStyles.inputContainer} onSubmit={handleSend}>
        <div style={{ ...ChatStyles.innerContainer, alignItems: "center" }}>
          <div style={{ ...ChatStyles.inputForm, width: "90%" }}>
            <textarea
              ref={chatInputRef}
              style={{ ...ChatStyles.textarea, width: "100%" }}
              placeholder="Type your message..."
              rows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />
            <button
              type="submit"
              style={ChatStyles.sendButton}
              aria-label="Send message"
              title="Send message"
              onMouseEnter={(e) => (e.currentTarget.style.color = "#19c28f")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#10a37f")}
            >
              <svg
                className="chat-input-send-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                width="20"
                height="20"
              >
                <path d="M2.01 21L23 12 2.01 3v7l15 2-15 2z" />
              </svg>
            </button>
          </div>
          {viewVectorStoresForChat && (
            <div style={{ textAlign: "center", marginTop: "0.5rem", width: "90%" }}>
              <DropDownBox
                  value={gridBoxValue}
                  valueExpr="Id"
                  deferRendering={false}
                  displayExpr="Name"
                  placeholder="Select..."
                  showClearButton={true}
                  dataSource={gridOptions}
                  onValueChanged={syncDataGridSelection}
                  contentRender={dataGridRender}
                  stylingMode="outlined"
                  labelMode="static"
                  label="Select Vector Store(s)"
                  style={{ width: "100%" }}
                  
                />
            </div>
          )}
          <div style={{ textAlign: "center", marginTop: "0.5rem", color: "#333" }}>
            Deployment: <b>{deployment || "-"}</b>,{" "}
            Company: <b>{company || "-"}</b>,{" "}
            Model: <b>{model || "-"}</b>
          </div>
        </div>
      </form>
      {/* Feedback Modal - shown when onOpenFeedback callback is not provided */}
      <FeedbackModal
        visible={showFeedbackModal}
        feedbackData={feedbackData}
        accessToken={accessToken}
        // The WebAPI now takes the rating: it writes it to the usage log beside the turn it rates, then
        // forwards it to the same feedback email the auth server used to send directly.
        apiBaseUrl={window._env_.REACT_APP_APIS}
        apiEndpoint={`/${data.AIUsage.submitFeedback}`}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedbackData(null);
        }}
      />
    </div>
  );
};

const TypingIndicator = () => {
  const dotStyle = {
    width: "8px",
    height: "8px",
    backgroundColor: "#5A954A",
    borderRadius: "50%",
    animationName: "blink",
    animationDuration: "1.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    marginRight: "0.3rem",
  };
  return (
    <div style={{ display: "flex", gap: "0.3rem", marginLeft: "1rem", paddingLeft: "1rem" }}>
      <span style={{ ...dotStyle, animationDelay: "0s" }} />
      <span style={{ ...dotStyle, animationDelay: "0.2s" }} />
      <span style={{ ...dotStyle, animationDelay: "0.4s" }} />
      <style>{`
        @keyframes blink {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const mapStateToProps = (state) => ({
  ReceiptReducer: state.ReceiptReducer,
  oidc: state.oidc,
});

export default connect(mapStateToProps)(Chat);
